/* eslint-disable @typescript-eslint/no-misused-promises */

import {
  Badge,
  BadgeProps,
  Button,
  Input,
  Typography,
  Colors,
  SectionHeader,
  Notification,
  NotificationProps,
  DropDownChip,
} from '@replai-platform/ui-components';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { ClipboardEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import TopNavPageTitle from '../../components/TopNavPageTitle';
import { RootState } from '../../store/rootReducer';
import useUploadVideosRequests from '../../api/hooks/uploadVideos/useUploadVideosRequests';

export const Divider = styled.div`
  width: 100%;
  height: 1px;
  margin-top: 1.25rem;
  margin-bottom: 1.5625rem;
  background-color: ${Colors.Gray[200]};
`;

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SourceList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(13rem, 1fr));
  gap: 0.75rem;
`;

const SourceTile = styled.div<{ $selected: boolean; $disabled: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border: 1px solid ${({ $selected }) => ($selected ? Colors.Primary[600] : Colors.Gray[200])};
  border-radius: 0.75rem;
  background-color: ${({ $selected, $disabled }) =>
    $selected ? Colors.Primary[25] : $disabled ? Colors.Gray[50] : Colors.Common.White};
  box-shadow: ${({ $selected }) => ($selected ? `0 0 0 1px ${Colors.Primary[600]}` : 'none')};
  color: ${({ $disabled }) => ($disabled ? Colors.Gray[500] : Colors.Gray[900])};
`;

const SourceLogo = styled.img<{ $disabled: boolean }>`
  width: 2rem;
  height: 2rem;
  object-fit: contain;
  ${({ $disabled }) => ($disabled ? 'filter: grayscale(100%); opacity: 0.5;' : '')}
`;

const SourceName = styled.span`
  flex: 1;
  font-size: 0.875rem;
  font-weight: 600;
`;

const AdAccountRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
`;

const LinkList = styled.ol`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const LinkRow = styled.li`
  display: grid;
  grid-template-columns: 1.5rem minmax(0, 1fr) auto;
  align-items: start;
  gap: 0.75rem;
`;

const LinkIndex = styled.span`
  height: 40px;
  line-height: 40px;
  font-size: 0.875rem;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  text-align: right;
  color: ${Colors.Gray[400]};
`;

const LinkActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 40px;
`;

const ListFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding-left: 2.25rem;
`;

const LinkCount = styled.span`
  font-size: 0.875rem;
  font-variant-numeric: tabular-nums;
  color: ${Colors.Gray[500]};
`;

const SubmitArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-top: 1.5rem;
  border-top: 1px solid ${Colors.Gray[200]};
`;

const MAX_LINKS = 10;
const GOOGLE_DRIVE_REGEX = /^https:\/\/drive\.google\.com\/.+$/;
const S3_NETWORK_LOGOS_URL = 'https://replai-public-assets.s3.amazonaws.com/network-logos';

const UPLOAD_SOURCES = [
  {
    type: 'GOOGLE_DRIVE',
    title: 'Google Drive',
    enabled: true,
    image: `${S3_NETWORK_LOGOS_URL}/google_drive.png`,
  },
  {
    type: 'FACEBOOK',
    title: 'Facebook',
    enabled: false,
    image: `${S3_NETWORK_LOGOS_URL}/facebook.png`,
  },
  {
    type: 'YOUTUBE',
    title: 'YouTube',
    enabled: false,
    image: `${S3_NETWORK_LOGOS_URL}/youtube.png`,
  },
];

const ALLOWED_ACCOUNTS_IDS: Record<string, { id: string; name: string }[]> = {
  '667dc444-79b8-4c12-a9c4-257efddc85bf': [
    { id: '1028145947312439', name: 'Candy Crush Saga' },
    { id: '1424494729743623', name: 'Candy Crush Saga - V2' },
    { id: '221021545162855', name: 'CCS - iOS - V3' },
    { id: '687283198065384', name: 'ASO Campaigns' },
  ],
  'c7fb7fe3-bbad-4331-8b0c-c0dc5ad99abb': [{ id: '106461736219514', name: 'Candy Crush Soda Saga' }],
  'e30b0583-b3e6-425b-80a2-15757efe468e': [{ id: '107342806058637', name: 'Farm Heroes Saga' }],
};

type LinkStatus = 'idle' | 'uploading' | 'success' | 'failed';

type SourceLink = {
  id: number;
  value: string;
  touched: boolean;
  status: LinkStatus;
};

type UploadStatus = {
  label: string;
  message: string;
  color: NotificationProps['color'];
  buttons?: NotificationProps['buttons'];
};

const LINK_STATUS_BADGES: Record<Exclude<LinkStatus, 'idle'>, { label: string; color: BadgeProps['color'] }> = {
  uploading: { label: 'Uploading', color: 'Primary' },
  success: { label: 'Queued', color: 'Success' },
  failed: { label: 'Failed', color: 'Error' },
};

const getLinkError = (link: SourceLink, links: SourceLink[]): string | null => {
  const value = link.value.trim();
  if (value.length === 0) {
    return null;
  }
  if (!GOOGLE_DRIVE_REGEX.test(value)) {
    return 'Enter a Google Drive folder link, starting with https://drive.google.com/';
  }
  const firstMatch = links.find((other) => other.value.trim() === value);
  if (firstMatch && firstMatch.id !== link.id) {
    return 'This folder is already in the list.';
  }
  return null;
};

const pluralizeFolders = (count: number) => `${count} ${count === 1 ? 'folder' : 'folders'}`;

const UploadVideos = () => {
  const projectId = useSelector((state: RootState) => state.project.id);
  const uploadVideosMutation = useUploadVideosRequests(projectId);
  const nextLinkId = useRef(1);
  const inputRefs = useRef(new Map<number, HTMLInputElement>());
  const [links, setLinks] = useState<SourceLink[]>([{ id: 0, value: '', touched: false, status: 'idle' }]);
  const [linkToFocus, setLinkToFocus] = useState<number | null>(null);
  const [pasteNote, setPasteNote] = useState<string | null>(null);
  const [adAccountId, setAdAccountId] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus | null>(null);

  const adAccounts = ALLOWED_ACCOUNTS_IDS[projectId] ?? [];
  const pendingLinks = links.filter((link) => link.status !== 'success' && link.value.trim().length > 0);
  const hasLinkErrors = links.some((link) => getLinkError(link, links) != null);
  const canSubmit = !isSubmitting && adAccountId != null && pendingLinks.length > 0 && !hasLinkErrors;

  // Projects with a single Ad Account get it preselected.
  useEffect(() => {
    const accounts = ALLOWED_ACCOUNTS_IDS[projectId] ?? [];
    setAdAccountId(accounts.length === 1 ? accounts[0].id : undefined);
  }, [projectId]);

  useEffect(() => {
    if (linkToFocus != null) {
      inputRefs.current.get(linkToFocus)?.focus();
      setLinkToFocus(null);
    }
  }, [linkToFocus]);

  const createLink = (value = '', touched = false): SourceLink => {
    const link = { id: nextLinkId.current, value, touched, status: 'idle' as const };
    nextLinkId.current += 1;
    return link;
  };

  const updateLink = (id: number, changes: Partial<SourceLink>) => {
    setLinks((current) => current.map((link) => (link.id === id ? { ...link, ...changes } : link)));
  };

  const addLink = () => {
    if (links.length >= MAX_LINKS) {
      return;
    }
    const link = createLink();
    setLinks((current) => [...current, link]);
    setLinkToFocus(link.id);
  };

  const removeLink = (id: number) => {
    setPasteNote(null);
    setLinks((current) => {
      if (current.length === 1) {
        return [createLink()];
      }
      return current.filter((link) => link.id !== id);
    });
  };

  const resetLinks = () => {
    setLinks([createLink()]);
    setUploadStatus(null);
    setPasteNote(null);
  };

  // Pasting several links at once (one per line, or separated by spaces/commas) fills one row per link.
  const onLinkPaste = (id: number, event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData
      .getData('text')
      .split(/[\s,]+/)
      .filter((value) => value.length > 0);
    if (pasted.length <= 1) {
      return;
    }
    event.preventDefault();

    const index = links.findIndex((link) => link.id === id);
    const available = MAX_LINKS - links.length + 1;
    const accepted = pasted.slice(0, available);
    const [first, ...rest] = accepted;
    const added = rest.map((value) => createLink(value, true));

    setLinks((current) => [
      ...current.slice(0, index),
      { ...current[index], value: first, touched: true, status: 'idle' },
      ...added,
      ...current.slice(index + 1),
    ]);
    setPasteNote(
      accepted.length < pasted.length
        ? `Added ${accepted.length} of ${pasted.length} pasted links. You can upload up to ${MAX_LINKS} at a time.`
        : null
    );
  };

  const onLinkKeyDown = (link: SourceLink, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') {
      return;
    }
    event.preventDefault();
    updateLink(link.id, { touched: true });
    const isLast = links[links.length - 1].id === link.id;
    if (isLast && link.value.trim().length > 0 && getLinkError(link, links) == null) {
      addLink();
    }
  };

  const onSubmit = async () => {
    if (!canSubmit || adAccountId == null) {
      return;
    }
    const toUpload = pendingLinks;
    const uploadIds = new Set(toUpload.map((link) => link.id));

    setIsSubmitting(true);
    setUploadStatus(null);
    setPasteNote(null);
    setLinks((current) => current.map((link) => (uploadIds.has(link.id) ? { ...link, status: 'uploading' } : link)));

    const results = await Promise.allSettled(
      toUpload.map((link) =>
        uploadVideosMutation
          .mutateAsync({ adAccountId, source: 'GOOGLE_DRIVE', sourceLink: link.value.trim() })
          .then(() => updateLink(link.id, { status: 'success' }))
          .catch((error) => {
            updateLink(link.id, { status: 'failed' });
            throw error;
          })
      )
    );

    const failedCount = results.filter((result) => result.status === 'rejected').length;
    const succeededCount = results.length - failedCount;

    if (failedCount === 0) {
      setUploadStatus({
        label: 'Uploading',
        message: `${pluralizeFolders(succeededCount)} queued. Videos will appear in your Ad Account within a few minutes.`,
        color: 'Success',
        buttons: [{ children: 'Upload more', variant: 'text', onClick: resetLinks }],
      });
    } else if (succeededCount === 0) {
      setUploadStatus({
        label: 'Failed',
        message: `We couldn't start the upload. Check that each link points to a Google Drive folder shared with replai@replai.io, then try again.`,
        color: 'Error',
      });
    } else {
      setUploadStatus({
        label: 'Partly uploaded',
        message: `${pluralizeFolders(succeededCount)} queued, ${failedCount} failed. Check the links marked Failed, then upload again to retry them.`,
        color: 'Warning',
      });
    }
    setIsSubmitting(false);
  };

  const submitLabel = (() => {
    if (isSubmitting) {
      return `Uploading ${pluralizeFolders(pendingLinks.length)}…`;
    }
    if (pendingLinks.length === 0) {
      return 'Upload';
    }
    return links.some((link) => link.status === 'failed')
      ? `Retry ${pluralizeFolders(pendingLinks.length)}`
      : `Upload ${pluralizeFolders(pendingLinks.length)}`;
  })();

  return (
    <>
      <TopNavPageTitle
        title="Upload Videos"
        subtitle="Upload your videos to your Facebook Ad Account."
        eventPrefix="upload-videos"
      />
      <Divider />
      <Form>
        <Section>
          <SectionHeader
            title="Videos Source"
            subtitle="Choose where your videos are stored."
            withDivider={false}
          />
          <SourceList>
            {UPLOAD_SOURCES.map((source) => (
              <SourceTile key={source.type} $selected={source.enabled} $disabled={!source.enabled}>
                <SourceLogo src={source.image} alt="" $disabled={!source.enabled} />
                <SourceName>{source.title}</SourceName>
                {source.enabled ? (
                  <Badge size="sm" color="Primary" leadingIcon="Check">
                    Selected
                  </Badge>
                ) : (
                  <Badge size="sm" color="Gray">
                    Not supported
                  </Badge>
                )}
              </SourceTile>
            ))}
          </SourceList>
        </Section>

        <AdAccountRow>
          <div>
            <Typography style={{ marginBottom: 0 }} fontWeight="semi-bold">
              Ad Account
            </Typography>
            <Typography type="text-sm" noMargin color={Colors.Gray[500]}>
              {adAccounts.length > 0
                ? 'Select the Facebook Ad Account you want to upload videos to.'
                : 'No Ad Accounts are set up for uploads in this project. Contact your Replai account manager to add one.'}
            </Typography>
          </div>
          <DropDownChip
            data-test="filter-account-dropdown"
            placeHolder="Select Account"
            defaultOption={adAccounts.find((account) => account.id === adAccountId)?.name}
            dropDownType="singleselect"
            dropDownAlignment="right"
            disabled={isSubmitting || adAccounts.length === 0}
            disableCrossButton
            dropDownOptions={adAccounts.map((account) => ({
              label: account.name,
              subLabel: `ID ${account.id}`,
              selected: account.id === adAccountId,
              onClick: () => {
                setAdAccountId(account.id);
              },
            }))}
          />
        </AdAccountRow>

        <Section aria-labelledby="upload-videos-links-title">
          <div>
            <Typography id="upload-videos-links-title" style={{ marginBottom: 0 }} fontWeight="semi-bold">
              Google Drive folders
            </Typography>
            <Typography type="text-sm" noMargin color={Colors.Gray[500]}>
              Add up to {MAX_LINKS} folder links, or paste several at once. Share each folder with replai@replai.io
              (viewer access is enough).
            </Typography>
          </div>

          <LinkList>
            {links.map((link, index) => {
              const error = getLinkError(link, links);
              const showError = error != null && link.touched;
              const isLocked = link.status === 'success' || link.status === 'uploading';
              const statusBadge = link.status === 'idle' ? null : LINK_STATUS_BADGES[link.status];

              return (
                <LinkRow key={link.id}>
                  <LinkIndex aria-hidden>{index + 1}</LinkIndex>
                  <Input
                    ref={(element) => {
                      if (element) {
                        inputRefs.current.set(link.id, element);
                      } else {
                        inputRefs.current.delete(link.id);
                      }
                    }}
                    aria-label={`Google Drive folder link ${index + 1}`}
                    value={link.value}
                    disabled={isLocked}
                    onChange={(e) => updateLink(link.id, { value: e.target.value, status: 'idle' })}
                    onBlur={() => updateLink(link.id, { touched: true })}
                    onPaste={(e) => onLinkPaste(link.id, e)}
                    onKeyDown={(e) => onLinkKeyDown(link, e)}
                    error={showError || link.status === 'failed'}
                    hintText={showError ? error ?? undefined : undefined}
                    fullWidth
                    placeholder="https://drive.google.com/drive/folders/..."
                    leadingIcon="Link"
                  />
                  <LinkActions>
                    {statusBadge && (
                      <Badge
                        size="sm"
                        color={statusBadge.color}
                        leadingIcon={link.status === 'success' ? 'Check' : undefined}
                      >
                        {statusBadge.label}
                      </Badge>
                    )}
                    <Button
                      variant="no-fill"
                      color="Gray"
                      centerIcon={{ name: 'Trash2', dimension: 16 }}
                      aria-label={`Remove link ${index + 1}`}
                      title="Remove link"
                      disabled={isSubmitting || (links.length === 1 && link.value.length === 0)}
                      onClick={() => removeLink(link.id)}
                    />
                  </LinkActions>
                </LinkRow>
              );
            })}
          </LinkList>

          <ListFooter>
            <Button
              variant="text"
              leadingIcon={{ name: 'Plus' }}
              disabled={isSubmitting || links.length >= MAX_LINKS}
              onClick={addLink}
              style={{ paddingLeft: 0 }}
            >
              {links.length >= MAX_LINKS ? 'Link limit reached' : 'Add another link'}
            </Button>
            <LinkCount aria-live="polite">
              {links.length} of {MAX_LINKS}
            </LinkCount>
          </ListFooter>
          {pasteNote && (
            <Typography type="text-sm" noMargin color={Colors.Warning[700]} style={{ paddingLeft: '2.25rem' }}>
              {pasteNote}
            </Typography>
          )}
        </Section>

        <SubmitArea>
          <Button
            variant="regular"
            onClick={onSubmit}
            leadingIcon={{ name: isSubmitting ? 'Loader' : 'Upload' }}
            disabled={!canSubmit}
            fullWidth
            style={{ justifyContent: 'center' }}
          >
            {submitLabel}
          </Button>
          {uploadStatus && (
            <Notification
              badgeLabel={uploadStatus.label}
              badgeTitle="Status"
              color={uploadStatus.color}
              message={uploadStatus.message}
              buttons={uploadStatus.buttons}
            />
          )}
        </SubmitArea>
      </Form>
    </>
  );
};

export default UploadVideos;
